import {Button, Form, FormButton, useTranslationContext, type FormProps} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useSyncExternalStore} from 'react';
import type {FieldValues} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {ApiError} from 'shared/web';

import type {BulkOperationActionCallbackParams} from '../../types/BulkOperationActionCallback';
import {getBulkOperationTarget} from '../../utils/createBulkOperationActionContext';
import {BulkOperationScopeField} from './BulkOperationScopeField';

type BulkOperationFormProps<T extends FieldValues> = Pick<
  FormProps<T>,
  'experimentalZodSchema' | 'defaultValues' | 'children'
> & {
  action: BulkOperationActionCallbackParams;
  onError: (error: ApiError) => void;
  toParams: (values: T) => Record<string, unknown> | null;
  isDisabled?: boolean;
};

export function BulkOperationForm<T extends FieldValues>(props: BulkOperationFormProps<T>) {
  const t = useTranslationContext();
  const context = props.action.context;
  const state = useSyncExternalStore(context.subscribe, context.getSnapshot);
  const scopeLabel =
    state.scope === 'selected' ? t('general.labels.selected') : t('page.datagrid.labels.filtered');
  const recordCount =
    state.scope === 'selected'
      ? state.selection.recordIds.length
      : (state.selection.query.totalCount ?? 0);
  const handleSubmit = async (values: T) => {
    await context.submit(props.toParams(values)).catch(props.onError);
  };
  return (
    <Form<T>
      experimentalZodSchema={props.experimentalZodSchema}
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
      isDisabled={state.isSubmitting}
    >
      {(control, formApi, navigateWithoutBlocker, withoutBlocker) => {
        const fields = props.children(control, formApi, navigateWithoutBlocker, withoutBlocker);
        return (
          <VStack spacing={6}>
            <Show
              when={
                isNotNilOrEmpty(props.action.actionDefinition.description)
                  ? props.action.actionDefinition.description
                  : null
              }
            >
              {(description) => <Text color="secondary">{description}</Text>}
            </Show>
            <BulkOperationScopeField context={context} />
            <Show when={fields}>
              {(content) => (
                <Box padding={5} backgroundColor="palettes.neutral.10.100" borderRadius="medium">
                  {content}
                </Box>
              )}
            </Show>
            <Box
              position="sticky"
              bottom={0}
              paddingTop={4}
              paddingBottom={2}
              backgroundColor="general.white"
              borderTop="thin solid"
              borderColor="general.separator"
            >
              <HStack justify="space-between" align="center" spacing={4} wrap>
                <Text size="small">
                  <strong>
                    {scopeLabel}: {recordCount}
                  </strong>
                </Text>
                <HStack spacing={3} wrap>
                  <Button
                    title={t('general.actions.cancel')}
                    variant="secondary"
                    onClick={context.close}
                    isDisabled={state.isSubmitting}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    title={t('general.actions.confirm')}
                    variant={
                      props.action.actionDefinition.variant === 'danger' ? 'danger' : 'primary'
                    }
                    isLoading={state.isSubmitting}
                    isDisabled={props.isDisabled || isNil(getBulkOperationTarget(state))}
                  />
                </HStack>
              </HStack>
            </Box>
          </VStack>
        );
      }}
    </Form>
  );
}
